import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
} from "@angular/core";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { Subject } from "rxjs";
import { EmbargoManagementPageDataService } from "../embargo-management-page-data.service";
import { NzMessageService } from "ng-zorro-antd/message";

interface Firm {
  id: number;
  name: string;
}

@Component({
  selector: "app-embargo-override-modal",
  templateUrl: "./embargo-override-modal.component.html",
  styleUrls: ["./embargo-override-modal.component.scss"],
})
export class EmbargoOverrideModalComponent implements OnInit {
  selectedSellSideFirm = null;
  selectedBuySideFirm = null;
  sellSideFirmList: Array<Firm> = [];
  buySideFirmList: Array<Firm> = [];
  modelData: number = null;
  modelDownload: number = null;
  researchDownload: number = null;
  showError = false;
  eventCode = ["Delete", "Backspace"];

  sellSideSearchTextchanged: Subject<string> = new Subject<string>();
  buySideSearchTextchanged: Subject<string> = new Subject<string>();

  @Input() showModal = false;
  @Input() embargoOverrideType;
  @Input() editEmbargoOverrideData;
  @Output() closeModal = new EventEmitter<boolean>();
  @Output() showWarning = new EventEmitter();
  @Output() triggerFetchGrid = new EventEmitter();

  constructor(
    private dataService: EmbargoManagementPageDataService,
    private message: NzMessageService,
  ) {}

  ngOnInit() {
    this.sellSideSearchTextchanged
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((text) => {
        this.getSellSideFirm(text);
      });

    this.buySideSearchTextchanged
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((text) => {
        this.getBuySideFirm(text);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.editEmbargoOverrideData &&
      changes.editEmbargoOverrideData.currentValue !==
        changes.editEmbargoOverrideData.previousValue
    ) {
      const {
        broker,
        brokerId,
        buyside,
        buysideId,
        modelData,
        modelDownload,
        researchDownload,
      } = changes.editEmbargoOverrideData.currentValue;

      this.selectedSellSideFirm = brokerId;
      this.selectedBuySideFirm = buysideId;
      brokerId
        ? this.sellSideFirmList.push({ id: brokerId, name: broker })
        : (this.sellSideFirmList = []);
      buysideId
        ? this.buySideFirmList.push({ id: buysideId, name: buyside })
        : (this.buySideFirmList = []);
      this.modelData = modelData;
      this.modelDownload = modelDownload;
      this.researchDownload = researchDownload;
    }
  }

  clear(type: string) {
    switch (type) {
      case "sellside":
        this.selectedSellSideFirm = null;
        this.sellSideFirmList = [];
        break;
      case "buyside":
        this.selectedBuySideFirm = null;
        this.buySideFirmList = [];
        break;
      default:
        break;
    }
  }

  handleCancelForEmbargoOverride() {
    this.showError = false;
    this.closeModal.emit(false);
  }

  handleSave() {
    if (this.validateInput()) {
      this.showError = false;
      if (this.embargoOverrideType === "Edit") {
        this.showWarning.emit(this.getOverrideData());
      } else {
        const obj = {
          type: 1,
          ...this.getOverrideData(),
        };
        this.dataService
          .postEmbargoDetails(
            obj.type,
            obj.buysideId,
            obj.brokerId,
            obj.modelData,
            obj.modelDownload,
            obj.researchDownload,
          )
          .subscribe(
            (data) => {
              this.triggerFetchGrid.emit();
              this.message.create("success", `${data}`);
            },
            (err) => {
              this.message.create("error", `${err["error"]["errors"]}`);
            },
          );
      }
    } else {
      this.showError = true;
    }
  }

  getOverrideData() {
    return {
      brokerId: this.selectedSellSideFirm || null,
      buysideId: this.selectedBuySideFirm || null,
      modelData: this.modelData,
      modelDownload: this.modelDownload,
      researchDownload: this.researchDownload,
    };
  }

  validateInput(): boolean {
    let isValid = false;
    if (
      this.selectedSellSideFirm !== null &&
      this.selectedBuySideFirm !== null
    ) {
      if (
        this.modelData === 0 ||
        this.modelData ||
        this.modelDownload === 0 ||
        this.modelDownload ||
        this.researchDownload === 0 ||
        this.researchDownload
      ) {
        isValid = true;
      }
    }

    return isValid;
  }

  searchSellSideFirm($event: string): void {
    this.sellSideSearchTextchanged.next($event);
  }

  searchBuySideFirm($event: string): void {
    this.buySideSearchTextchanged.next($event);
  }

  getSellSideFirm($event: string) {
    const obj = {
      type: "sell",
      name: $event,
    };
    if ($event.length >= 2) {
      this.dataService
        .getFirmList(obj.type, obj.name)
        .subscribe((data: Array<Firm>) => {
          this.showHideEmptyDropDownContentEL(true);
          this.sellSideFirmList = data;
        });
    }
  }

  getBuySideFirm($event) {
    const obj = {
      type: "buy",
      name: $event,
    };
    if ($event.length >= 2) {
      this.dataService
        .getFirmList(obj.type, obj.name)
        .subscribe((data: Array<Firm>) => {
          this.showHideEmptyDropDownContentEL(true);
          this.buySideFirmList = data;
        });
    }
  }

  handleModelChange($event, changeFor) {
    if (
      this.eventCode.includes($event.code) &&
      changeFor === "broker" &&
      $event.target.value.length < 2
    ) {
      this.sellSideFirmList = [];
    } else if (
      this.eventCode.includes($event.code) &&
      changeFor === "buyside" &&
      $event.target.value.length < 2
    ) {
      this.buySideFirmList = [];
    }
  }

  showHideEmptyDropDownContentEL = (show) => {
    const el = document.querySelectorAll(
      ".ant-select-dropdown-menu-item.ant-select-dropdown-menu-item-disabled",
    );
    if (el.length) {
      el[0]["style"]["display"] = show ? "block" : "none";
    }
  };

  handleOpenChange = (event, changeFor) => {
    if (changeFor === "broker" && !event && !this.selectedSellSideFirm) {
      this.sellSideFirmList = [];
    } else if (changeFor === "buyside" && !event && !this.selectedBuySideFirm) {
      this.buySideFirmList = [];
    }
  };
}
